/**
 * Geocodificador especializado para patrimonio cultural de Andalucía
 * 
 * Conecta con servicios del IAPH (Instituto Andaluz del Patrimonio Histórico) para acceder a:
 * - Base de datos MOSAICO (100,000+ bienes culturales)
 * - 27,000+ registros patrimonio inmueble georeferenciado
 * - Museos, bibliotecas, sitios arqueológicos, monumentos
 * 
 * Fuentes oficiales:
 * - WFS DERA Cultural: https://www.ideandalucia.es/services/DERA_g09_cultura/wfs
 * - Datos Abiertos Cultura: https://www.juntadeandalucia.es/datosabiertos/portal/
 * - IAPH MOSAICO: https://guiadigital.iaph.es/
 * 
 * @module services/geocoding/specialized
 */

import type { 
  WFSFeature, 
  WFSSearchOptions 
} from './WFSBaseGeocoder';
import { 
  WFSBaseGeocoder 
} from './WFSBaseGeocoder';
import type { GeoJSONFeature } from '../../../types/geojson';
import { getStringProp } from '../../../types/geojson';
import type { SpecializedGeocoderConfig, GeocodingResult } from '../../../types/infrastructure';

/**
 * Tipos de patrimonio cultural en Andalucía
 */
export enum CulturalFacilityType {
  /** Museos (~180 en Andalucía) */
  MUSEUM = 'MUSEO',
  
  /** Bibliotecas (~1,135 recursos bibliotecarios) */
  LIBRARY = 'BIBLIOTECA',
  
  /** Teatros y auditorios */
  THEATER = 'TEATRO',
  
  /** Centros culturales */
  CULTURAL_CENTER = 'CENTRO_CULTURAL',
  
  /** Monumentos y patrimonio arquitectónico */
  MONUMENT = 'MONUMENTO',
  
  /** Sitios arqueológicos */
  ARCHAEOLOGICAL = 'ARQUEOLOGICO',
  
  /** Archivos históricos */
  ARCHIVE = 'ARCHIVO'
}

/**
 * Opciones de búsqueda específicas para patrimonio cultural
 */
export interface CulturalSearchOptions extends WFSSearchOptions {
  /** Tipo de bien cultural */
  facilityType?: CulturalFacilityType;
  
  /** Código de protección BIC, etc */
  protectionCode?: string;
  
  /** Período histórico */
  period?: string;
}

/**
 * Geocodificador especializado para patrimonio cultural andaluz
 * 
 * Precisión esperada: ±5-15m (coordenadas oficiales IAPH/IECA)
 * Cobertura: ~7,000+ sitios patrimonio cultural georeferenciado
 * 
 * @example
 * ```typescript
 * const geocoder = new WFSCulturalGeocoder();
 * const result = await geocoder.geocode({
 *   name: 'Museo de la Alhambra',
 *   municipality: 'Granada',
 *   province: 'Granada'
 * });
 * // result.confidence >= 80 (match oficial IAPH)
 * ```
 */
export class WFSCulturalGeocoder extends WFSBaseGeocoder {
  
  /**
   * Configuración específica para WFS cultural.
   *
   * MIGRACIÓN: DERA_g09_cultura/wfs devuelve HTTP 404 desde 2026-03.
   * Migrado a DERA_g12_servicios que sí incluye equipamientos culturales:
   * - g12_20_Museo     → Museos
   * - g12_09_ArchivoBiblioteca → Archivos y Bibliotecas
   * El servicio g09_03_teatro y g09_04_monumento no tienen equivalente
   * directo; se usa la capa genérica g12_20_Museo como fallback.
   */
  protected getDefaultConfig(): SpecializedGeocoderConfig {
    return {
      wfsEndpoint: 'https://www.ideandalucia.es/services/DERA_g12_servicios/wfs',
      layerName: 'g12_20_Museo',
      fuzzyThreshold: 0.35,
      timeout: 15000,
      outputSRS: 'EPSG:25830'
    };
  }

  /**
   * Parsea feature GeoJSON de DERA G09 Cultural
   * 
   * Estructura esperada DERA G09:
   * {
   *   type: "Feature",
   *   geometry: { type: "Point", coordinates: [x, y] },
   *   properties: {
   *     DENOMINACION: "Museo de la Alhambra",
   *     TIPO: "Museo",
   *     MUNICIPIO: "Granada",
   *     PROVINCIA: "Granada",
   *     DIRECCION: "C/ Real de la Alhambra s/n",
   *     CODIGO_BIC: "RI-51-0000479",
   *     PERIODO: "Nazarí",
   *     TITULARIDAD: "Público"
   *   }
   * }
   */
  protected parseFeature(feature: GeoJSONFeature): WFSFeature | null {
    try {
      const props = feature.properties || {};
      const geom = feature.geometry;

      // Validar geometría vía parseGeometry (Point + MultiPoint N=1 — sub-0a)
      const pt = this.parseGeometry(geom, `Cultural feature ${String(feature.id ?? 'sin-id')}`);
      if (!pt) {
        console.warn('Feature cultural sin geometría válida:', feature.id);
        return null;
      }

      const [x, y] = pt;

      // Validar coordenadas UTM30 Andalucía
      if (x < 100000 || x > 800000 || y < 4000000 || y > 4300000) {
        console.warn('Coordenadas fuera de rango UTM30:', x, y);
        return null;
      }

      // Schema DERA G12 actual (smoke 16-may sub-WDGSDC): claves minúsculas.
      // Mantenemos fallback defensivo a mayúsculas legacy ISE para anti-regresión
      // (HX-FALLBACK-DEFENSIVO, 3.ª aplicación cross-geocoder tras WSSD/Health).
      // `tipo` solo lo expone g12_09_ArchivoBiblioteca — en Museo llega como ''.
      // `CODIGO_BIC`, `PERIODO`, `TITULARIDAD` ya no existen en el schema; quedan ''
      // pero se preservan vía `...props` si DERA los reintroduce en el futuro.
      return {
        name: getStringProp(props, 'nombre', 'DENOMINACION', 'NOMBRE'),
        x,
        y,
        municipality: getStringProp(props, 'municipio', 'localidad', 'MUNICIPIO', 'LOCALIDAD'),
        province: getStringProp(props, 'provincia', 'PROVINCIA'),
        address: getStringProp(props, 'direccion', 'DIRECCION', 'DOMICILIO'),
        properties: {
          type: getStringProp(props, 'tipo', 'TIPO'),
          protectionCode: getStringProp(props, 'CODIGO_BIC', 'CODIGO'),
          period: getStringProp(props, 'PERIODO', 'EPOCA'),
          ownership: getStringProp(props, 'TITULARIDAD'),
          phone: getStringProp(props, 'telefono', 'TELEFONO'),
          website: getStringProp(props, 'web', 'WEB', 'URL'),
          ...props
        }
      };
    } catch (error) {
      console.error('Error parseando feature cultural:', error);
      return null;
    }
  }

  /**
   * Construye filtro CQL específico para patrimonio cultural.
   *
   * sub-WDGSDC (OBS-FT-WFS-DERA-G12-SCHEMA-DRIFT-CROSS, 16-may): override completo
   * sin `super.buildCQLFilter()` para emitir nombres de campo en minúsculas y
   * priorizar `cod_mun='X'` cuando se aporta `codMun`.
   *
   * Whitelist `tipo` (HX-WHITELIST-FIELD-PER-LAYER):
   * - `g12_09_ArchivoBiblioteca` SÍ expone `tipo` → emitir filtro
   * - `g12_20_Museo` NO tiene `tipo` → emitir filtro produce HTTP 400
   * - `CODIGO_BIC`, `PERIODO` ya no existen en el schema actual → eliminados
   *   (si options.protectionCode/options.period se aportan, se descartan)
   */
  protected buildCQLFilter(options: CulturalSearchOptions): string {
    const filters: string[] = [];

    // Filtro municipal: cod_mun (INE) preferente sobre municipio ILIKE
    if (options.codMun) {
      filters.push(`cod_mun='${this.escapeCQL(options.codMun)}'`);
    } else if (options.municipality) {
      filters.push(`municipio ILIKE '%${this.escapeCQL(options.municipality)}%'`);
    }

    // Filtro provincial
    if (options.province) {
      filters.push(`provincia ILIKE '%${this.escapeCQL(options.province)}%'`);
    }

    // Filtro BBOX (heredado de la base, sintaxis idéntica)
    if (options.bbox) {
      const [minX, minY, maxX, maxY] = options.bbox;
      filters.push(`BBOX(the_geom,${minX},${minY},${maxX},${maxY},'EPSG:25830')`);
    }

    // Whitelist `tipo`: SOLO Archivo/Biblioteca expone el campo (smoke 16-may sub-WDGSDC).
    // En g12_20_Museo emitirlo provoca HTTP 400.
    if (options.facilityType && this.config.layerName === 'g12_09_ArchivoBiblioteca') {
      const typeValue = this.getFacilityTypeValue(options.facilityType);
      filters.push(`tipo ILIKE '%${this.escapeCQL(typeValue)}%'`);
    }

    // CODIGO_BIC, PERIODO eliminados del schema DERA G12 actual (sub-WDGSDC 16-may).
    // options.protectionCode + options.period se descartan silenciosamente.

    return filters.length > 0 ? filters.join(' AND ') : '';
  }

  /**
   * Convierte tipo de facilidad a valor para WFS
   */
  private getFacilityTypeValue(type: CulturalFacilityType): string {
    const map: Record<CulturalFacilityType, string> = {
      [CulturalFacilityType.MUSEUM]: 'Museo',
      [CulturalFacilityType.LIBRARY]: 'Biblioteca',
      [CulturalFacilityType.THEATER]: 'Teatro',
      [CulturalFacilityType.CULTURAL_CENTER]: 'Centro Cultural',
      [CulturalFacilityType.MONUMENT]: 'Monumento',
      [CulturalFacilityType.ARCHAEOLOGICAL]: 'Yacimiento',
      [CulturalFacilityType.ARCHIVE]: 'Archivo'
    };
    return map[type] || '';
  }

  /**
   * Geocodifica cambiando automáticamente entre capas según tipo detectado.
   *
   * MIGRACIÓN 2026-03: DERA_g09_cultura/wfs → HTTP 404 (servicio eliminado).
   * Las capas culturales están ahora en DERA_g12_servicios:
   * - g12_20_Museo              → Museos
   * - g12_09_ArchivoBiblioteca  → Archivos y Bibliotecas
   */
  public async geocodeWithAutoLayer(options: CulturalSearchOptions): Promise<GeocodingResult | null> {
    const nameLower = options.name.toLowerCase();

    const layerPriority: string[] = [];

    if (nameLower.includes('museo')) {
      layerPriority.push('g12_20_Museo');
    }
    if (nameLower.includes('biblioteca') || nameLower.includes('archivo')) {
      layerPriority.push('g12_09_ArchivoBiblioteca');
    }
    // Teatro, monumento, centro cultural → intentar ambas capas disponibles
    if (layerPriority.length === 0) {
      layerPriority.push('g12_20_Museo', 'g12_09_ArchivoBiblioteca');
    }

    for (const layer of layerPriority) {
      this.config.layerName = layer;
      const result = await this.geocodeLegacy(options);
      if (result && result.confidence >= 70) {
        this.config.layerName = 'g12_20_Museo';
        return result;
      }
    }

    this.config.layerName = 'g12_20_Museo';
    return null;
  }

  /**
   * Busca patrimonio cultural por código BIC
   * (Bien de Interés Cultural)
   */
  public async geocodeByProtectionCode(code: string): Promise<GeocodingResult | null> {
    try {
      const params = this.buildWFSParams({
        name: '',
        protectionCode: code,
        maxResults: 10
      } as CulturalSearchOptions);

      const response = await this.axiosInstance.get(this.config.wfsEndpoint, { params });
      const features = this.parseResponse(response.data);

      if (features.length === 0) {
        return null;
      }

      // Si hay match exacto de código, confianza 100%
      const feature = features[0];
      return this.featureToGeocodingResult(feature, 1.0, feature.name);

    } catch (error) {
      console.error(`Error geocodificando por código BIC ${code}:`, error);
      return null;
    }
  }

  /**
   * Obtiene todo el patrimonio cultural de un municipio
   * Útil para análisis territorial completo
   */
  public async getAllCulturalSitesInMunicipality(
    municipality: string,
    province?: string
  ): Promise<WFSFeature[]> {
    const allFeatures: WFSFeature[] = [];

    // Consultar las capas culturales disponibles en DERA_g12_servicios
    const layers = [
      'g12_20_Museo',
      'g12_09_ArchivoBiblioteca',
      'g12_20_Museo',    // fallback para teatros (sin capa g09_03 en g12)
      'g12_20_Museo'     // fallback para monumentos (sin capa g09_04 en g12)
    ];

    for (const layer of layers) {
      try {
        this.config.layerName = layer;
        
        const params = this.buildWFSParams({
          name: '',
          municipality,
          province,
          maxResults: 500
        });

        const response = await this.axiosInstance.get(this.config.wfsEndpoint, { params });
        const features = this.parseResponse(response.data);
        allFeatures.push(...features);

      } catch (error) {
        console.error(`Error obteniendo ${layer} de ${municipality}:`, error);
      }
    }

    // Restaurar capa por defecto (g12_20_Museo tras migración g09→g12)
    this.config.layerName = 'g12_20_Museo';
    
    return allFeatures;
  }

  /**
   * Busca patrimonio por período histórico
   * Útil para planificación emergencias en zonas históricas
   */
  public async geocodeByPeriod(
    municipality: string,
    period: string
  ): Promise<WFSFeature[]> {
    try {
      const params = this.buildWFSParams({
        name: '',
        municipality,
        period,
        maxResults: 500
      } as CulturalSearchOptions);

      const response = await this.axiosInstance.get(this.config.wfsEndpoint, { params });
      return this.parseResponse(response.data);

    } catch (error) {
      console.error(`Error buscando patrimonio período ${period}:`, error);
      return [];
    }
  }
}
